import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
  ConditionalOperator,
  Rule,
  RuleAccessType,
  RuleTarget,
  SegmentationType,
  TargetType
} from './segmentation-config.types';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { PURGE } from 'redux-persist';
import { RootState } from 'app/store';
import { TabKey } from './segmentation-config-tab-keys';

const targetTypeOptionsByCategory: Record<TabKey, TargetType[]> = {
  [TabKey.Forms]: [
    TargetType.UTM_MEDIUM,
    TargetType.UTM_SOURCE,
    TargetType.UTM_CAMPAIGN,
    TargetType.REFERRER_HOST_NAME,
    TargetType.PAYLOAD_KEY,
    TargetType.LANDING_PAGE_FULL_URL,
    TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEY_AND_VALUE,
    TargetType.PAYLOAD_KEY_AND_VALUE
  ],
  [TabKey.Cost]: [
    TargetType.AD_PLATFORM,
    TargetType.CAMPAIGN_NAME,
    TargetType.LABEL_NAME,
    TargetType.LABEL_TYPE,
    TargetType.CAMPAIGN_ADVERTISING_CHANNEL_TYPE,
    TargetType.PUBLISHER_PLATFORM,
    TargetType.CAMPAIGN_TYPE,
    TargetType.AD_SET_NAME,
    TargetType.AD_NAME
  ]
};

const initialState = {
  segmentationConfigName: '',
  segmentName: '',
  segmentValue: '',
  segmentationType: SegmentationType.FILTER,
  ruleAccessType: RuleAccessType.ADMIN,
  rules: [{ id: 1 }] as Rule[],
  ruleTargets: [
    {
      rule_id: 1,
      target_id: 1,
      value: '',
      key: '',
      type: TargetType.UTM_MEDIUM,
      conditional_operator: ConditionalOperator.EQUALS
    }
  ] as RuleTarget[],
  segmentNameErrors: [],
  segmentationConfigNameErrors: [],
  segmentationCategory: null as TabKey | null
};

const segmentationConfigSlice = createSlice({
  name: 'segmentationConfig',
  initialState,
  reducers: {
    setSegmentationCategory: (state, action) => {
      state.segmentationCategory = action.payload;
    },
    onSegmentationConfigNameChanged: (state, action) => {
      const { newSegmentationConfigName } = action.payload;
      state.segmentationConfigName = newSegmentationConfigName;
    },
    onSegmentNameChanged: (state, action) => {
      const { newSegmentName } = action.payload;
      state.segmentName = newSegmentName;
    },
    onSegmentValueChanged: (state, action) => {
      const { newSegmentValue } = action.payload;
      state.segmentValue = newSegmentValue;
    },
    onSegmentationTypeChanged: (state, action) => {
      const { newSegmentationType } = action.payload;
      state.segmentationType = newSegmentationType;
    },
    onRuleAccessTypeChanged: (state, action) => {
      const { newRuleAccessType } = action.payload;
      state.ruleAccessType = newRuleAccessType;
    },
    onSegmentationConfigNameErrorsChanged: (state, action) => {
      const { newSegmentationConfigNameErrors } = action.payload;
      state.segmentationConfigNameErrors = newSegmentationConfigNameErrors;
    },
    onSegmentNameErrorsChanged: (state, action) => {
      const { newSegmentNameErrors } = action.payload;
      state.segmentNameErrors = newSegmentNameErrors;
    },
    onRuleAdded: (state) => {
      const newRuleId = state.rules[state.rules.length - 1].id + 1;
      state.rules.push({ id: newRuleId });
      state.ruleTargets.push({
        rule_id: newRuleId,
        target_id: 1,
        key: '',
        value: '',
        type: TargetType.UTM_MEDIUM,
        conditional_operator: ConditionalOperator.EQUALS
      });
    },
    onRulesChanged: (state, action) => {
      const { rules } = action.payload;
      state.rules = rules;
    },
    onRuleRemoved: (state, action) => {
      const { ruleKey } = action.payload;
      state.rules = state.rules.filter((item) => item.id !== ruleKey);
      state.ruleTargets = state.ruleTargets.filter(
        (item) => item.rule_id !== ruleKey
      );
    },
    onTargetAdded: (state, action) => {
      const { ruleKey } = action.payload;
      const newTargetKey =
        state.ruleTargets.filter((target) => target.rule_id === ruleKey)
          .length + 1;

      state.ruleTargets.push({
        rule_id: ruleKey,
        target_id: newTargetKey,
        value: '',
        type: TargetType.UTM_MEDIUM,
        conditional_operator: ConditionalOperator.EQUALS
      });
    },
    onTargetsChanged: (state, action) => {
      const { targets } = action.payload;
      state.ruleTargets = targets;
    },
    onTargetRemoved: (state, action) => {
      const { ruleKey, targetKey } = action.payload;
      state.ruleTargets = state.ruleTargets.filter(
        (item) => !(item.rule_id === ruleKey && item.target_id === targetKey)
      );
    },
    updateRuleTarget: (state, action) => {
      const { ruleKey, targetKey, props } = action.payload;
      state.ruleTargets = state.ruleTargets.map((item) =>
        item.rule_id === ruleKey && item.target_id === targetKey
          ? { ...item, ...props }
          : item
      );
    },
    onRuleTargetsChanged: (state, action) => {
      const { ruleTargets } = action.payload;
      state.ruleTargets = ruleTargets;
    },
    resetSegmentationConfig: (state) => {
      Object.assign(state, initialState);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addCase(selectSite, () => initialState);
  }
});

const { actions, reducer } = segmentationConfigSlice;

export const {
  onSegmentationConfigNameChanged,
  onSegmentNameChanged,
  onSegmentValueChanged,
  onSegmentationTypeChanged,
  onRuleAccessTypeChanged,
  onSegmentationConfigNameErrorsChanged,
  onSegmentNameErrorsChanged,
  onRuleAdded,
  onRulesChanged,
  onRuleRemoved,
  onTargetAdded,
  onTargetsChanged,
  onTargetRemoved,
  updateRuleTarget,
  onRuleTargetsChanged,
  resetSegmentationConfig,
  setSegmentationCategory
} = actions;

export default reducer;

export const selectSegmentationConfigState = (state: RootState) =>
  state.segmentationConfig;

export const selectSegmentationCategory = (state: RootState) =>
  state.segmentationConfig.segmentationCategory;

export const selectTargetTypeOptions = (state: RootState) => {
  return state.segmentationConfig.segmentationCategory
    ? targetTypeOptionsByCategory[state.segmentationConfig.segmentationCategory]
    : [];
};

export const selectRules = createSelector(
  selectSegmentationConfigState,
  (state) => state.rules
);

export const selectRuleTargets = createSelector(
  selectSegmentationConfigState,
  (state) => state.ruleTargets
);

export const selectConfigName = createSelector(
  selectSegmentationConfigState,
  (state) => state.segmentationConfigName
);

export const selectSegmentName = createSelector(
  selectSegmentationConfigState,
  (state) => state.segmentName
);

export const selectSegmentValue = createSelector(
  selectSegmentationConfigState,
  (state) => state.segmentValue
);

export const selectSegmentationType = createSelector(
  selectSegmentationConfigState,
  (state) => state.segmentationType
);

export const selectRuleAccessType = createSelector(
  selectSegmentationConfigState,
  (state) => state.ruleAccessType
);

export const selectConfigNameErrors = createSelector(
  selectSegmentationConfigState,
  (state): string[] => state.segmentationConfigNameErrors
);

export const selectSegmentNameErrors = createSelector(
  selectSegmentationConfigState,
  (state): string[] => state.segmentNameErrors
);

export const selectRuleTargetsByRuleId = createSelector(
  (state: RootState) => state.segmentationConfig.ruleTargets,
  (_: RootState, ruleId: number) => ruleId,
  (ruleTargets, ruleId) =>
    ruleTargets?.filter((target) => target.rule_id === ruleId) || []
);
